/**
 * Created by Todd Yu on 05/20/2020.
 * Description:
 * Main layout for Administrative Console; responsible for routing.
 * ------ maintenance history ------
 * Modified by Teddy Ding on 12/15/20. Add notetype and entitytype feature.
 * Modified by Lucas Wang on 12/15/20. Refine the code to match coding standard.
 * Updated by Daniel Wang on 2/13/2024. Refactor the layout by new design
 */

import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { Store, select } from '@ngrx/store';

import { adminPagesConstants, adminPageRoutingMap, adminPageFeatures } from '../admin-page-setting.constants';
import { AdminPageSetting } from '../admin-page-setting.model';
import { AppState } from '../../../redux';
import { GetCredentials } from '../../../redux/actions/admin-page.actions';
import { AaclKeywordSearchOptions } from 'aacl-keyword-search';

@Component({
    selector: 'tam-admin-page-layout',
    templateUrl: './admin-page-layout.component.html',
    styleUrls: ['./admin-page-layout.component.scss']
})
export class AdminPageLayoutComponent implements OnInit, OnDestroy {
    adminPages: Array<AdminPageSetting> = [];
    keyword = '';
    keywordSearchOptions: AaclKeywordSearchOptions = new AaclKeywordSearchOptions('', 250, 'Search');
    selectedPage: AdminPageSetting;
    pages = [];

    private _destroySubscriptions: Array<Subscription> = [];

    constructor(
        private _router: Router,
        private _store: Store<AppState>
    ) { }

    ngOnInit() {
        // Initialize each admin page from constants
        Object.keys(adminPagesConstants).forEach(page => {
            if (adminPageFeatures.includes(page)) {
                this.adminPages.push(adminPagesConstants[page]);
            }
        });
        // rebuild the pages
        this._rebuildAdminPages(this.adminPages);

        this._destroySubscriptions.push(
            // Initialize keyword search
            this.keywordSearchOptions.feedbackSubject$.subscribe(
                feedback => this.keyword = feedback.payload.data
            ),
            // Subscribe to store changes when new page is selected
            this._store.pipe(
                select('adminPage')
            ).subscribe(() => {
                this._setSelectedPage();
            })
        );

        this._store.dispatch(new GetCredentials());
    }

    ngOnDestroy(): void {
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
        this._destroySubscriptions = [];
    }

    /** Called upon selecting a new page; updates the 'active' CSS class and router */
    onPageClick(page: AdminPageSetting) {
        this.selectedPage = page;
        const pageUrl: string = page.routingUrl;
        this._router.navigate(['settings', 'admin-console', pageUrl]);
    }

    private _rebuildAdminPages(pages) {
        const grouped = pages.reduce((res, curr) => {
            res[curr.group] = res[curr.group] || [];
            res[curr.group].push(curr);
            return res;
        }, {});

        for (const group of Object.values(grouped)) {
            (group as [{ index: number }]).sort((a, b) => a.index - b.index);
        }

        this.pages = Object.entries(grouped);
    }

    // set selected page style by the url
    private _setSelectedPage(): void {
        const url = this._router.url;
        const pageIndex = url.lastIndexOf('/');
        const currentPage = url.substring(pageIndex + 1);
        this.selectedPage = adminPageRoutingMap.get(currentPage);
    }
}
